import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, Disc3 } from "lucide-react";

function fmt(seconds) {
  if (!isFinite(seconds) || seconds < 0) return "00:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export default function AudioReelPlayer({ src, label = "Escucha aquí nuestro reel de audio logos" }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => setCurrent(a.currentTime);
    const onMeta = () => setDuration(a.duration || 0);
    const onEnd = () => setPlaying(false);
    const onPlay = () => {
      // Notificar a otros medios que se pausen
      window.dispatchEvent(new CustomEvent("kilombo:media-play", { detail: "audio-reel" }));
      // Pausar iframes Vimeo/YouTube directamente en la página
      document.querySelectorAll("iframe").forEach((f) => {
        try {
          if (/vimeo\.com/.test(f.src)) f.contentWindow.postMessage('{"method":"pause"}', "*");
          else if (/youtube/.test(f.src)) f.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', "*");
        } catch (_) {}
      });
    };
    const onExternal = (e) => {
      if (e.detail !== "audio-reel" && !a.paused) {
        a.pause();
        setPlaying(false);
      }
    };
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("ended", onEnd);
    a.addEventListener("play", onPlay);
    window.addEventListener("kilombo:media-play", onExternal);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("ended", onEnd);
      a.removeEventListener("play", onPlay);
      window.removeEventListener("kilombo:media-play", onExternal);
    };
  }, []);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      a.play();
      setPlaying(true);
    } else {
      a.pause();
      setPlaying(false);
    }
  };

  const onSeek = (e) => {
    const a = audioRef.current;
    if (!a || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX ?? (e.touches && e.touches[0]?.clientX) ?? 0) - rect.left;
    const pct = Math.max(0, Math.min(1, x / rect.width));
    a.currentTime = pct * duration;
    setCurrent(a.currentTime);
  };

  const progress = duration ? (current / duration) * 100 : 0;

  return (
    <div className="relative w-full rounded-2xl border border-white/10 bg-gradient-to-br from-neutral-950 via-black to-neutral-950 p-5 md:p-6 overflow-hidden">
      {/* Ambient glow */}
      <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-[#ff6a2b]/15 blur-3xl pointer-events-none" />

      <div className="relative flex items-center gap-4 md:gap-6">
        {/* Vinyl / disc icon */}
        <button
          onClick={toggle}
          aria-label={playing ? "Pausar" : "Reproducir"}
          className="relative shrink-0 w-14 h-14 md:w-16 md:h-16 rounded-full bg-gradient-to-br from-[#ffa957] to-[#ff6a2b] flex items-center justify-center shadow-lg shadow-[#ff6a2b]/30 hover:shadow-[#ff6a2b]/50 transition-all group"
        >
          <span className={`absolute inset-0 rounded-full ${playing ? "animate-spin" : ""}`} style={{ animationDuration: "6s" }}>
            <Disc3 className="w-full h-full text-black/25" />
          </span>
          <span className="relative w-9 h-9 md:w-10 md:h-10 rounded-full bg-black text-white flex items-center justify-center">
            {playing ? <Pause className="w-4 h-4" fill="currentColor" /> : <Play className="w-4 h-4 ml-0.5" fill="currentColor" />}
          </span>
        </button>

        {/* Label + timeline */}
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-3">
            <p className="font-mono-caps text-xs md:text-sm tracking-[0.15em] uppercase text-white/85 truncate">{label}</p>
            <span className="font-mono-caps text-xs text-white/50 tabular-nums shrink-0">
              {fmt(current)} <span className="text-white/25">/ {fmt(duration)}</span>
            </span>
          </div>

          <div
            onClick={onSeek}
            className="mt-3 h-2 rounded-full bg-white/10 cursor-pointer relative overflow-hidden group"
          >
            <div
              className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-[#ffa957] to-[#ff6a2b] transition-[width] duration-150"
              style={{ width: `${progress}%` }}
            />
            <div
              className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-white shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ left: `calc(${progress}% - 6px)` }}
            />
          </div>
        </div>
      </div>

      <audio ref={audioRef} src={src} preload="metadata" />
    </div>
  );
}
